import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Descriptions, Divider, Form, Input, Layout, Modal, Select, Switch, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import EmployeeTable from '../components/EmployeeTable';
import { createEmployee, fetchEmployees, updateEmployee } from '../redux/slices/employeeSlice';
import employeeService from '../services/employeeService';
import organizationService from '../services/organizationService';
import roleService from '../services/roleService';
import userService from '../services/userService';
import { canManageEmployees, isSuperAdmin } from '../utils/permissions';
import './Employees.css';

function Employees() {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [form] = Form.useForm();
  const { user } = useSelector((state) => state.auth);
  const { isLoading } = useSelector((state) => state.employees);
  const [organizations, setOrganizations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableManagers, setAvailableManagers] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(user?.organizationId || undefined);

  const selectedFormOrganizationId = Form.useWatch('organizationId', form);
  const isUserExists = Form.useWatch('isUserExists', form);

  const effectiveOrganizationId = useMemo(
    () => (isSuperAdmin(user) ? selectedOrganizationId : user?.organizationId),
    [selectedOrganizationId, user]
  );

  const effectiveFormOrganizationId = useMemo(
    () => (isSuperAdmin(user) ? selectedFormOrganizationId : user?.organizationId),
    [selectedFormOrganizationId, user]
  );

  const employeeFilters = useMemo(
    () => (effectiveOrganizationId ? { organizationId: effectiveOrganizationId } : {}),
    [effectiveOrganizationId]
  );

  const managerOptions = useMemo(() => {
    const managers = Array.isArray(availableManagers) ? availableManagers : [];

    return managers
      .filter((manager) => manager?.id && manager.id !== user?.employeeId)
      .map((manager) => ({
        label: `${manager.firstName} ${manager.lastName} (${manager.employeeCode})`,
        value: manager.id
      }));
  }, [availableManagers, user]);

  useEffect(() => {
    const loadOrganizations = async () => {
      if (!isSuperAdmin(user)) {
        return;
      }

      try {
        const response = await organizationService.getAll(1, 1000);
        setOrganizations(response.data || []);
      } catch (error) {
        message.error(error.response?.data?.message || 'Failed to load organizations');
      }
    };

    loadOrganizations();
  }, [user]);

  useEffect(() => {
    const loadSupportingData = async () => {
      if (!effectiveFormOrganizationId) {
        setRoles([]);
        setAvailableUsers([]);
        setAvailableManagers([]);
        return;
      }

      try {
        const [rolesResponse, usersResponse, employeesResponse] = await Promise.all([
          roleService.getAll(1, 100, { organizationId: effectiveFormOrganizationId }),
          userService.getAll(1, 100, { organizationId: effectiveFormOrganizationId }),
          employeeService.getAll(1, 1000, {
            organizationId: effectiveFormOrganizationId,
            status: 'Active'
          })
        ]);

        setRoles(Array.isArray(rolesResponse?.data) ? rolesResponse.data : []);
        setAvailableUsers(Array.isArray(usersResponse?.data) ? usersResponse.data : []);
        setAvailableManagers(Array.isArray(employeesResponse?.data?.data) ? employeesResponse.data.data : []);
      } catch (error) {
        message.error(error.response?.data?.message || 'Failed to load user dependencies');
      }
    };

    loadSupportingData();
  }, [effectiveFormOrganizationId]);

  const handleEdit = (id) => {
    loadEmployeeDetails(id, 'edit');
  };

  const handleView = (id) => {
    loadEmployeeDetails(id, 'view');
  };

  const loadEmployeeDetails = async (id, mode) => {
    try {
      const response = await employeeService.getById(id);
      const employee = response?.data?.data || response?.data;
      setSelectedEmployee(employee || null);

      if (mode === 'view') {
        setIsViewModalOpen(true);
        return;
      }

      setModalMode('edit');
      form.setFieldsValue({
        organizationId: employee?.organizationId,
        employeeCode: employee?.employeeCode,
        firstName: employee?.firstName,
        lastName: employee?.lastName,
        email: employee?.email,
        phone: employee?.phone,
        gender: employee?.gender || undefined,
        designation: employee?.designation,
        joinDate: employee?.joinDate ? dayjs(employee.joinDate) : null,
        dateOfBirth: employee?.dateOfBirth ? dayjs(employee.dateOfBirth) : null,
        bankAccountNumber: employee?.bankAccountNumber,
        bankIFSC: employee?.bankIFSC,
        panNumber: employee?.panNumber,
        aadharNumber: employee?.aadharNumber,
        managerId: employee?.managerId || null,
        isUserExists: !!employee?.userId,
        userId: employee?.userId || undefined
      });
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error loading employee details:', error);
      message.error(error.response?.data?.message || 'Failed to load employee details');
    }
  };

  const handleCreateEmployee = async (values) => {
    try {
      const payload = {
        organizationId: isSuperAdmin(user) ? values.organizationId : user?.organizationId,
        employeeCode: values.employeeCode,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        gender: values.gender,
        designation: values.designation,
        joinDate: values.joinDate.format('YYYY-MM-DD'),
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format('YYYY-MM-DD') : null,
        bankAccountNumber: values.bankAccountNumber || null,
        bankIFSC: values.bankIFSC || null,
        panNumber: values.panNumber || null,
        aadharNumber: values.aadharNumber || null,
        managerId: values.managerId || null,
        isUserExists: values.isUserExists || false,
        userId: values.isUserExists ? values.userId : null,
        userData: values.isUserExists ? null : {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone || null,
          roleId: values.userRoleId
        }
      };

      if (modalMode === 'edit' && selectedEmployee) {
        await dispatch(updateEmployee({ id: selectedEmployee.id, data: payload })).unwrap();
        message.success('Employee updated successfully');
      } else {
        const response = await dispatch(createEmployee(payload)).unwrap();
        const defaultPassword = response?.meta?.defaultPassword;
        message.success(
          defaultPassword
            ? `Employee created successfully. Default password: ${defaultPassword}`
            : 'Employee created successfully'
        );
      }

      dispatch(fetchEmployees({ page: 1, limit: 10, filters: employeeFilters }));
      form.resetFields();
      setSelectedEmployee(null);
      setModalMode('create');
      setIsModalOpen(false);
    } catch (error) {
      message.error(error || 'Failed to create employee');
    }
  };

  return (
    <Layout style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Employee Management</h1>
        {canManageEmployees(user) && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setModalMode('create');
              setSelectedEmployee(null);
              form.resetFields();
              form.setFieldsValue({
                organizationId: isSuperAdmin(user) ? selectedOrganizationId : user?.organizationId,
                isUserExists: true
              });
              setIsModalOpen(true);
            }}
          >
            Add Employee
          </Button>
        )}
      </div>
      {!canManageEmployees(user) && (
        <Alert
          type="info"
          message="This role has read-only access to employee data."
          style={{ marginBottom: 16 }}
        />
      )}
      {isSuperAdmin(user) && (
        <div style={{ marginBottom: '16px' }}>
          <Select
            placeholder="Select organization"
            value={selectedOrganizationId}
            onChange={setSelectedOrganizationId}
            options={organizations.map((organization) => ({
              label: organization.name,
              value: organization.id
            }))}
            style={{ width: 320 }}
          />
        </div>
      )}
      <EmployeeTable
        onEdit={handleEdit}
        onView={handleView}
        filters={employeeFilters}
      />
      <Modal
        title="Employee Details"
        open={isViewModalOpen}
        footer={null}
        onCancel={() => {
          setIsViewModalOpen(false);
          setSelectedEmployee(null);
        }}
        width={720}
      >
        {selectedEmployee && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Employee Code">{selectedEmployee.employeeCode || '-'}</Descriptions.Item>
            <Descriptions.Item label="Name">{`${selectedEmployee.firstName || ''} ${selectedEmployee.lastName || ''}`.trim() || '-'}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedEmployee.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="Phone">{selectedEmployee.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="Designation">{selectedEmployee.designation || '-'}</Descriptions.Item>
            <Descriptions.Item label="Manager">{selectedEmployee.manager ? `${selectedEmployee.manager.firstName} ${selectedEmployee.manager.lastName}` : '-'}</Descriptions.Item>
            <Descriptions.Item label="Join Date">{selectedEmployee.joinDate ? dayjs(selectedEmployee.joinDate).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
            <Descriptions.Item label="Date of Birth">{selectedEmployee.dateOfBirth ? dayjs(selectedEmployee.dateOfBirth).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
            <Descriptions.Item label="Status">{selectedEmployee.status || '-'}</Descriptions.Item>
            <Descriptions.Item label="Gender">{selectedEmployee.gender || '-'}</Descriptions.Item>
            <Descriptions.Item label="Bank Account">{selectedEmployee.bankAccountNumber || '-'}</Descriptions.Item>
            <Descriptions.Item label="IFSC">{selectedEmployee.bankIFSC || '-'}</Descriptions.Item>
            <Descriptions.Item label="PAN">{selectedEmployee.panNumber || '-'}</Descriptions.Item>
            <Descriptions.Item label="Aadhar">{selectedEmployee.aadharNumber || '-'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
      <Modal
        title={modalMode === 'edit' ? 'Edit Employee' : 'Add Employee'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedEmployee(null);
          setModalMode('create');
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText={modalMode === 'edit' ? 'Update Employee' : 'Create Employee'}
        confirmLoading={isLoading}
        width={760}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateEmployee}
          initialValues={{
            joinDate: dayjs(),
            gender: undefined,
            isUserExists: true,
            organizationId: isSuperAdmin(user) ? selectedOrganizationId : user?.organizationId
          }}
        >
          {isSuperAdmin(user) && (
            <Form.Item
              label="Organization"
              name="organizationId"
              rules={[{ required: true, message: 'Please select organization' }]}
            >
              <Select
                placeholder="Select organization"
                options={organizations.map((organization) => ({
                  label: organization.name,
                  value: organization.id
                }))}
              />
            </Form.Item>
          )}

          <Form.Item label="Is User Exists" name="isUserExists" valuePropName="checked">
            <Switch
              checkedChildren="Yes"
              unCheckedChildren="No"
              disabled={modalMode === 'edit'}
            />
          </Form.Item>

          {isUserExists && (
            <Form.Item
              label="Select User"
              name="userId"
              rules={[{ required: true, message: 'Please select existing user' }]}
            >
              <Select
                showSearch
                placeholder="Select user"
                optionFilterProp="label"
                disabled={modalMode === 'edit'}
                options={availableUsers.map((existingUser) => ({
                  label: `${existingUser.firstName} ${existingUser.lastName} (${existingUser.email})`,
                  value: existingUser.id
                }))}
              />
            </Form.Item>
          )}

          {!isUserExists && (
            <>
              <Divider orientation="left">User Account Details</Divider>
              <Alert
                type="info"
                style={{ marginBottom: 16 }}
                message="A new user will be created first, then linked to the employee. Default password will be FirstNameLastName."
              />
              <Form.Item
                label="User Role"
                name="userRoleId"
                rules={[{ required: true, message: 'Please select user role' }]}
              >
                <Select
                  placeholder="Select role"
                  options={roles.map((role) => ({
                    label: `${role.name} (${role.roleType})`,
                    value: role.id
                  }))}
                />
              </Form.Item>
            </>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
            <Form.Item
              label="Employee Code"
              name="employeeCode"
              rules={[{ required: true, message: 'Please enter employee code' }]}
            >
              <Input placeholder="EMP001" />
            </Form.Item>

            <Form.Item
              label="Official Email"
              name="email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input placeholder="employee@company.com" />
            </Form.Item>

            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item label="Phone" name="phone">
              <Input />
            </Form.Item>

            <Form.Item label="Gender" name="gender">
              <Select
                allowClear
                options={[
                  { label: 'Male', value: 'Male' },
                  { label: 'Female', value: 'Female' },
                  { label: 'Other', value: 'Other' }
                ]}
              />
            </Form.Item>

            <Form.Item label="Designation" name="designation">
              <Input />
            </Form.Item>

            <Form.Item label="Manager" name="managerId">
              <Select
                allowClear
                showSearch
                placeholder="Select manager"
                optionFilterProp="label"
                options={managerOptions}
              />
            </Form.Item>

            <Form.Item
              label="Join Date"
              name="joinDate"
              rules={[{ required: true, message: 'Please select join date' }]}
            >
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>

            <Form.Item label="Date of Birth" name="dateOfBirth">
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>

            <Form.Item label="Bank Account Number" name="bankAccountNumber">
              <Input />
            </Form.Item>

            <Form.Item label="Bank IFSC" name="bankIFSC">
              <Input />
            </Form.Item>

            <Form.Item label="PAN Number" name="panNumber">
              <Input />
            </Form.Item>

            <Form.Item label="Aadhar Number" name="aadharNumber">
              <Input />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </Layout>
  );
}

export default Employees;
